import type { CSSVariablesResolver } from "@mantine/core";
import type { ThemeProps } from "./useTheme";

/**
 * Mantine derives the "light" variant background and text from shade 9, which
 * ends near black in our color palettes. Rebuild them from the mid shades
 * instead. color="dark" only gets the new background: its default text is
 * dark-0, which is what neutral icons need.
 */
const accentColors = ["orange", "red", "teal"];

function wash(color: string, opacity: number) {
  return `color-mix(in srgb, var(--mantine-color-${color}-3) ${opacity}%, transparent)`;
}

export const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    "--mantine-secondary-color-filled": `var(--mantine-color-${(theme as ThemeProps).secondaryColor}-filled)`,
  },
  light: {
    "--app-background": "var(--mantine-color-base-1)",
    "--mantine-color-default-border": "var(--mantine-color-gray-3)",
    "--mantine-color-default-border-hover": "var(--mantine-color-gray-4)",
    ...Object.fromEntries(
      accentColors.flatMap((color) => [
        [
          `--mantine-color-${color}-light-hover`,
          `color-mix(in srgb, var(--mantine-color-${color}-2) 40%, var(--mantine-color-${color}-1))`,
        ],
        [
          `--mantine-color-${color}-light-color`,
          `var(--mantine-color-${color}-5)`,
        ],
      ]),
    ),
  },
  dark: {
    "--app-background": "var(--mantine-color-body)",
    "--app-recessed":
      "color-mix(in srgb, var(--mantine-color-dark-8), var(--mantine-color-dark-7))",
    "--mantine-color-error": "var(--mantine-color-red-5)",
    "--mantine-color-disabled": "var(--mantine-color-dark-5)",
    "--mantine-color-default-border-hover": "var(--mantine-color-dark-3)",
    ...Object.fromEntries(
      [...accentColors, "dark"].flatMap((color) => [
        [`--mantine-color-${color}-light`, wash(color, 15)],
        [`--mantine-color-${color}-light-hover`, wash(color, 22)],
        ...(accentColors.includes(color)
          ? [
              [
                `--mantine-color-${color}-light-color`,
                `var(--mantine-color-${color}-2)`,
              ],
            ]
          : []),
      ]),
    ),
  },
});
